import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Drawer, Grid } from '@material-ui/core';
import { OfferPageTabs } from 'core/store/offer-store';
import { useFormikContext } from 'formik';
import { FarmRiskType } from '@ourbranch/lookups';
import flat from 'flat';

import useSession from 'core/hooks/use-session';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useStore } from 'core/store';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import Field from 'core/components/form/form.v2';
import { ActionButton } from 'core/components/action-button';
import Button from 'core/components/button/button';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import { NotificationCard } from 'core/components/notification-card';
import closeIcon from 'core/assets/svg/x.svg';
import arrowIcon from 'core/assets/svg/ic-goto.svg';
import useStyles from './farm-navigator.styles';

function FarmNavigator({ isNavigatorOpen, closeNavigator, location }) {
  const { canEditFarmPolicyAdjustment } = useSession();
  const { offer: offerStore } = useStore();
  const { isStale, isOutdated, setCurrentTab } = offerStore;
  const { values, errors } = useFormikContext();
  const { farmLocations, farmCoverage } = values;
  const policyLevelAdjustmentFactor = farmCoverage?.policyLevelAdjustmentFactor ?? 0;
  const { disableForm, setActiveForm } = useActiveFormState('FarmNavigator');
  const { inPolicyRoute, inOfferEditRoute } = useRouteHelper();

  // Farm underwriting approval details
  const approvalDetails = offerStore.offer?.quote?.preBindUWCheck?.approvalDetails;
  const underwriterApprovedAt = approvalDetails?.underwriterApprovedAt;
  const sentToUnderwritingAt = approvalDetails?.sentToUnderwritingAt;

  // Calculate footer height based on the same logic as footer.jsx
  const footerHeight = useMemo(() => {
    if (inPolicyRoute) {
      return 98;
    }
    if (underwriterApprovedAt || sentToUnderwritingAt) {
      return 130;
    }
    if (isStale && !isOutdated) {
      return 180;
    }
    return 98;
  }, [inPolicyRoute, isStale, isOutdated, underwriterApprovedAt, sentToUnderwritingAt]);
  const classes = useStyles({ footerHeight });

  // Helper function to clean numeric fields that might be NaN
  const cleanNumericField = (value, defaultValue = 0) => {
    if (Number.isNaN(value)) {
      return defaultValue;
    }
    return value ?? defaultValue;
  };

  // Memoize calculated total adjustment factors to reduce re-renders
  const adjustmentFactorTotals = useMemo(() => {
    const totals = {};
    farmLocations?.forEach((location, locationIndex) => {
      location?.risks?.forEach((risk, riskIndex) => {
        const key = `farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactorTotal`;
        const riskAdjustment = cleanNumericField(risk.adjustmentFactor, 0);
        const policyAdjustment = cleanNumericField(policyLevelAdjustmentFactor, 0);
        totals[key] = riskAdjustment + policyAdjustment;
      });
    });
    return totals;
  }, [farmLocations, policyLevelAdjustmentFactor]);

  const showErrorBanner = useMemo(() => {
    return errors && Object.keys(flat(errors)).some((key) => key.includes('adjustmentFactor'));
  }, [errors]);

  const navigateToLocationOrRisk = (linkId) => {
    // if we are on offer, set the tab to FARM
    if (inOfferEditRoute) {
      setCurrentTab(OfferPageTabs.FARM);
    }

    const scrollTo = (id) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return !!el;
    };

    if (scrollTo(linkId)) {
      return;
    }

    setTimeout(() => {
      if (scrollTo(linkId)) {
        return;
      }
      const locationId = linkId.match(/^farmLocations\[\d+\]/)?.[0];
      if (locationId && locationId !== linkId) {
        scrollTo(locationId);
      }
    }, 250);
  };

  return (
    <Drawer
      anchor="left"
      open={isNavigatorOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          closeNavigator();
        }
      }}
      classes={{ paper: classes.drawerPaper }}
      variant="persistent"
    >
      <div className={classes.drawerContent}>
        <div className={classes.header}>
          <Label type="subtitleSmall"> Navigate this {location} </Label>
          <ActionButton
            type="edit"
            size="medium"
            icon={closeIcon}
            onClick={closeNavigator}
            className={classes.closeButton}
          />
        </div>
        {canEditFarmPolicyAdjustment && (
          <div className={classes.policyLevelAdjustor}>
            <div className={classes.headerRow}>
              <Label
                type="body1Bold"
                className={classNames({ [classes.headerTopMargin]: canEditFarmPolicyAdjustment })}
              >
                Policy-level adjustments
              </Label>
              <Grid item xs={3}>
                <LabelTooltip
                  label="Total"
                  tooltip={{
                    onHoverText:
                      'This total reflects the overall percentage adjustment applied at the policy level. It cascades down to all individual risks. The total adjustment must stay within plus or minus 99%. '
                  }}
                  mode="light"
                  labelClassName={classes.label}
                  containerClassName={classes.labelContainer}
                >
                  <Field
                    name="farmCoverage.policyLevelAdjustmentFactor"
                    id="farmCoverage.policyLevelAdjustmentFactor"
                    type="numeric"
                    mode="light"
                    hideErrorMessage
                    fast={false}
                    format={{ suffix: '%', decimalScale: 0 }}
                    disabled={disableForm}
                    onChange={() => setActiveForm('FarmNavigator')}
                    className={classes.adjustmentField}
                  />
                </LabelTooltip>
              </Grid>
            </div>

            {showErrorBanner && (
              <div className={classes.headerRow}>
                <NotificationCard
                  className={classes.notification}
                  iconSize="icon16"
                  type="warning"
                  header="One or more of your adjustments is missing or outside of the allowable range of plus or minus 99%."
                >
                  <Label>Please correct all errors to continue.</Label>
                </NotificationCard>
              </div>
            )}

            {disableForm && (
              <div className={classes.headerRow}>
                <NotificationCard
                  className={classes.notification}
                  iconSize="icon16"
                  type="warning"
                  header="This form is disabled until you update the offer."
                >
                  <Label>Please update the offer price to continue.</Label>
                </NotificationCard>
              </div>
            )}
          </div>
        )}
        <div className={classes.scrollableContent}>
          {farmLocations?.map((location, locationIndex) => {
            const locationIdString = `farmLocations[${locationIndex}]`;
            return (
              // location container
              <div key={locationIdString} className={classes.location}>
                <Button onClick={() => navigateToLocationOrRisk(locationIdString)} className={classes.navigateButton}>
                  <Label type="body1Bold"> {location.description} </Label>
                  <span className={classes.hoverText}>
                    Go
                    <img className={classes.arrowIcon} src={arrowIcon} alt="Go To Icon" />
                  </span>
                </Button>

                {/* map through the risks for each location */}
                {location?.risks?.map((risk, riskIndex) => {
                  let riskIdString;
                  if (risk.type === FarmRiskType.Fo6Scheduled || risk.type === FarmRiskType.Fo6Unscheduled) {
                    riskIdString = `${locationIdString}.${risk.type}`;
                  } else {
                    riskIdString = `${locationIdString}.risks[${riskIndex}]`;
                  }

                  return (
                    <div key={riskIdString} className={classes.risk}>
                      <Button
                        onClick={() => navigateToLocationOrRisk(riskIdString)}
                        className={classNames(classes.navigateButton, {
                          [classes.topMargin]: riskIndex === 0 && canEditFarmPolicyAdjustment
                        })}
                      >
                        <Label type="body2" className={classes.riskDescription}>
                          {risk.description}
                        </Label>
                        <span className={classes.hoverText}>
                          Go
                          <img className={classes.arrowIcon} src={arrowIcon} alt="Go To Icon" />
                        </span>
                      </Button>
                      {canEditFarmPolicyAdjustment && (
                        <>
                          {riskIndex === 0 ? (
                            <Grid item xs={3} className={classes.noPadding}>
                              <LabelTooltip
                                label="Risk Level"
                                tooltip={{
                                  onHoverText:
                                    'You can adjust each risk-level by plus or minus 99%. These changes will be combined with the policy-level adjustment for your total impact.'
                                }}
                                mode="light"
                                labelClassName={classes.label}
                                containerClassName={classes.labelContainer}
                              >
                                <Field
                                  name={`farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactor`}
                                  id={`farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactor`}
                                  type="numeric"
                                  mode="light"
                                  fast={false}
                                  format={{ suffix: '%', decimalScale: 0 }}
                                  disabled={disableForm}
                                  onChange={() => setActiveForm('FarmNavigator')}
                                  className={classes.adjustmentField}
                                  hideErrorMessage
                                />
                              </LabelTooltip>
                            </Grid>
                          ) : (
                            <Field
                              xs={3}
                              name={`farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactor`}
                              id={`farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactor`}
                              type="numeric"
                              mode="light"
                              fast={false}
                              format={{ suffix: '%', decimalScale: 0 }}
                              disabled={disableForm}
                              onChange={() => setActiveForm('FarmNavigator')}
                              className={classes.adjustmentField}
                              hideErrorMessage
                            />
                          )}
                          {riskIndex === 0 ? (
                            <Grid item xs={3} className={classes.noPadding}>
                              <LabelTooltip
                                label="Total"
                                tooltip={{
                                  onHoverText:
                                    'This column shows the final total impact after combining both your policy-level and risk-level adjustments. It gives you the complete percentage change for this item.'
                                }}
                                mode="light"
                                labelClassName={classes.label}
                                containerClassName={classes.labelContainer}
                              >
                                <Field
                                  name={`farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactorTotal`}
                                  id={`farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactorTotal`}
                                  type="numeric"
                                  mode="light"
                                  value={
                                    adjustmentFactorTotals[
                                      `farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactorTotal`
                                    ]
                                  }
                                  format={{ suffix: '%', decimalScale: 0 }}
                                  disabled
                                  className={classes.adjustmentField}
                                  hideErrorMessage
                                />
                              </LabelTooltip>
                            </Grid>
                          ) : (
                            <Field
                              xs={3}
                              name={`farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactorTotal`}
                              id={`farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactorTotal`}
                              type="numeric"
                              mode="light"
                              value={
                                adjustmentFactorTotals[
                                  `farmLocations[${locationIndex}].risks[${riskIndex}].adjustmentFactorTotal`
                                ]
                              }
                              format={{ suffix: '%', decimalScale: 0 }}
                              disabled
                              className={classes.adjustmentField}
                              hideErrorMessage
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </Drawer>
  );
}
FarmNavigator.propTypes = {
  isNavigatorOpen: PropTypes.bool.isRequired,
  closeNavigator: PropTypes.func.isRequired,
  location: PropTypes.string.isRequired
};

export default FarmNavigator;
